/**
 * Verification Progress Component
 * Phase 6: User Experience Enhancements - Verification Journey Progress
 */

import React, { useState, useEffect } from 'react';
import { FiCheckCircle, FiCircle, FiUpload, FiUser, FiMapPin, FiBriefcase } from 'react-icons/fi';
import { User } from '@/types/auth';
import { verificationService } from '@/services/verificationService';

interface VerificationProgressProps {
    user: User;
    className?: string;
}

interface VerificationStep {
    id: string;
    label: string;
    icon: React.ElementType;
    completed: boolean;
    description: string;
}

interface DocumentCategory {
    identity: boolean;
    address: boolean;
    business: boolean;
}

const VerificationProgress: React.FC<VerificationProgressProps> = ({
    user,
    className = ''
}) => {
    const [documentCategories, setDocumentCategories] = useState<DocumentCategory>({
        identity: false,
        address: false,
        business: false
    });
    const [loading, setLoading] = useState(true);

    // Fetch user documents and calculate verification status
    useEffect(() => {
        const fetchVerificationStatus = async () => {
            try {
                setLoading(true);
                const documentsResponse = await verificationService.getUserDocuments();

                if (documentsResponse.success && documentsResponse.documents) {
                    const documents = documentsResponse.documents;

                    // Check if each category has at least one verified document
                    const categories: DocumentCategory = {
                        identity: documents.some(doc =>
                            doc.documentCategory === 'identity' && doc.status === 'verified'
                        ),
                        address: documents.some(doc =>
                            doc.documentCategory === 'address' && doc.status === 'verified'
                        ),
                        business: documents.some(doc =>
                            doc.documentCategory === 'business' && doc.status === 'verified'
                        )
                    };

                    setDocumentCategories(categories);
                }
            } catch (error) {
                console.error('Failed to fetch verification status:', error);
                // Keep the default false values on error
            } finally {
                setLoading(false);
            }
        };

        fetchVerificationStatus();
    }, []);

    // Calculate completion based on actual document verification status
    const getVerificationSteps = (): VerificationStep[] => {
        const steps: VerificationStep[] = [
            {
                id: 'identity',
                label: 'Identity Verification',
                icon: FiUser,
                completed: documentCategories.identity,
                description: 'Upload government-issued ID'
            },
            {
                id: 'address',
                label: 'Address Verification',
                icon: FiMapPin,
                completed: documentCategories.address,
                description: 'Verify your residential address'
            }
        ];

        // Add business verification for employers
        if (user.role === 'employer') {
            steps.push({
                id: 'business',
                label: 'Business Verification',
                icon: FiBriefcase,
                completed: documentCategories.business,
                description: 'Verify your business credentials'
            });
        }

        return steps;
    };

    const steps = getVerificationSteps();
    const completedSteps = steps.filter(step => step.completed).length;
    const totalSteps = steps.length;
    const completionPercentage = Math.round((completedSteps / totalSteps) * 100);

    const isFullyVerified = completionPercentage === 100;

    // Show loading state
    if (loading) {
        return (
            <div className={`bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm ${className}`}>
                <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                            Verification Progress
                        </h3>
                        <div className="w-16 h-6 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-4">
                        <div className="h-2 bg-gray-300 dark:bg-gray-600 rounded-full animate-pulse" style={{ width: '30%' }}></div>
                    </div>
                    <div className="w-3/4 h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mb-4"></div>
                </div>
                <div className="space-y-3">
                    {[1, 2].map((index) => (
                        <div key={index} className="flex items-center space-x-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                            <div className="w-8 h-8 bg-gray-300 dark:bg-gray-600 rounded-full animate-pulse"></div>
                            <div className="flex-1">
                                <div className="w-1/2 h-4 bg-gray-200 dark:bg-gray-600 rounded animate-pulse mb-2"></div>
                                <div className="w-3/4 h-3 bg-gray-200 dark:bg-gray-600 rounded animate-pulse"></div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className={`bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm ${className}`}>
            <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Verification Progress
                    </h3>
                    <span className={`text-sm font-medium px-2 py-1 rounded ${isFullyVerified
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400'
                        : 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400'
                        }`}>
                        {completionPercentage}% Complete
                    </span>
                </div>

                {/* Progress Bar */}
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-4">
                    <div
                        className={`h-2 rounded-full transition-all duration-500 ${isFullyVerified
                            ? 'bg-green-600'
                            : 'bg-blue-600'
                            }`}
                        style={{ width: `${completionPercentage}%` }}
                    ></div>
                </div>

                {/* Completion Status */}
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    {isFullyVerified
                        ? '🎉 Verification complete! You now have unlimited access.'
                        : `Complete ${totalSteps - completedSteps} more step${totalSteps - completedSteps === 1 ? '' : 's'} to unlock unlimited access.`
                    }
                </p>
            </div>

            {/* Verification Steps */}
            <div className="space-y-3">
                {steps.map((step, index) => {
                    const StepIcon = step.icon;
                    return (
                        <div
                            key={step.id}
                            className={`flex items-center space-x-3 p-3 rounded-lg transition-colors ${step.completed
                                ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                                : 'bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600'
                                }`}
                        >
                            <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${step.completed
                                ? 'bg-green-600 text-white'
                                : 'bg-gray-300 dark:bg-gray-600 text-gray-600 dark:text-gray-400'
                                }`}>
                                {step.completed ? (
                                    <FiCheckCircle className="w-4 h-4" />
                                ) : (
                                    <StepIcon className="w-4 h-4" />
                                )}
                            </div>

                            <div className="flex-1">
                                <div className="flex items-center justify-between">
                                    <h4 className={`font-medium ${step.completed
                                        ? 'text-green-900 dark:text-green-100'
                                        : 'text-gray-900 dark:text-white'
                                        }`}>
                                        {step.label}
                                    </h4>
                                    {step.completed && (
                                        <span className="text-xs text-green-600 dark:text-green-400 font-medium">
                                            ✓ Verified
                                        </span>
                                    )}
                                </div>
                                <p className={`text-xs mt-1 ${step.completed
                                    ? 'text-green-700 dark:text-green-300'
                                    : 'text-gray-600 dark:text-gray-400'
                                    }`}>
                                    {step.description}
                                </p>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Next Steps */}
            {!isFullyVerified && (
                <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                    <div className="flex items-start space-x-2">
                        <FiUpload className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                        <div>
                            <h5 className="text-sm font-medium text-blue-900 dark:text-blue-100">
                                Next Step
                            </h5>
                            <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                                {(() => {
                                    const nextStep = steps.find(step => !step.completed);
                                    return nextStep ? `Complete ${nextStep.label.toLowerCase()}` : 'All steps completed!';
                                })()}
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default VerificationProgress;